import i18next from 'i18next';
import { AppProps } from '../../@types/AppProps';
import { State } from '../../@types/State';
import ButtonArrowRight from '../../Components/Button/ButtonArrowRight';
import HtmlNode from '../../Components/Extension/HtmlNode';
import InputFieldRegistrationNumber from '../../Components/Input/InputFieldRegistrationNumber';
import getVehicle from '../../Http/getVehicle';
import { RequestError } from '../../Http/http';
import validation from './validation';
import { getSymbolLogotype } from '../../Templates/Logotype';
import { Vehicle } from '../../@types/Vehicle';
import RequestStatusError from './RequestStatusError';
import { MarketCode } from '../../@types/MarketCode';

const INPUT_REGISTRATION_NUMBER = 'input-registration-number';
const INPUT_REGISTRATION_NUMBER_NODE = `${INPUT_REGISTRATION_NUMBER}-node`;
const BUTTON_PROCEED_NODE = 'button-proceed-node';
const REQUEST_ERROR_NODE = 'waykevaluation-req-error-node';
const EXPANDED_CLASSNAME = 'waykevaluation-init--expanded';

interface PreviewStateLocal {
  value: {
    registrationNumber: string;
  };
  validation: {
    registrationNumber: boolean;
  };
  interaction: {
    registrationNumber: boolean;
  };
}

type NameTypes = keyof PreviewStateLocal['value'];

const createState = (_value: State, marketCode?: MarketCode): PreviewStateLocal => {
  const value = { ..._value };
  return {
    value,
    validation: {
      registrationNumber: validation.registrationNumber(value.registrationNumber, marketCode),
    },
    interaction: { registrationNumber: false },
  };
};

interface PreviewProps {
  state: State;
  vehicle?: Vehicle;
  settings: AppProps;
  onProceed: (state: State, vehicle: Vehicle) => void;
}

class Preview extends HtmlNode {
  private readonly props: PreviewProps;
  private contexts: {
    registrationNumber?: InputFieldRegistrationNumber;
    buttonProceed?: ButtonArrowRight;
    requestError?: RequestStatusError;
  } = {};
  private expanded?: boolean;
  private state: PreviewStateLocal;
  private requestError?: RequestError;
  private requestErrorNode?: HTMLElement;
  private vehicle?: Vehicle;
  private settings: AppProps;

  constructor(element: HTMLElement, props: PreviewProps) {
    super(element);
    this.props = props;
    this.state = createState(props.state, props.settings.marketCode);
    this.vehicle = props.vehicle;
    this.settings = props.settings;

    const sessionStorageExpanded = sessionStorage.getItem('wayke-valuation-web-expanded');
    this.expanded = sessionStorageExpanded
      ? sessionStorageExpanded === 'expanded'
        ? true
        : sessionStorageExpanded === 'minimized'
        ? false
        : props.settings.defaultExpanded
      : props.settings.defaultExpanded;

    this.render();
  }

  private onChange(e: Event) {
    const currentTarget = e.currentTarget as HTMLInputElement;
    const name = currentTarget.name as NameTypes;
    const value = currentTarget.value.toUpperCase();

    this.state.value[name] = value;
    currentTarget.value = value;
    this.state.validation[name] = validation[name](value, this.settings.marketCode);
    this.updateUiError(name);
    this.updateProceedButton();
  }

  private onBlur(e: Event) {
    const currentTarget = e.currentTarget as HTMLInputElement;
    const name = currentTarget.name as NameTypes;

    this.state.interaction[name] = true;
    this.state.validation[name] = validation[name](
      this.state.value[name],
      this.settings.marketCode
    );
    this.updateUiError(name);
    this.updateProceedButton();
  }

  private onKeyDown(e: KeyboardEvent) {
    if (e.key == 'Enter') {
      if (this.state.validation.registrationNumber) {
        this.onGetVehicle();
      } else {
        this.onBlur(e);
      }
    }
  }

  private updateUiError(name: NameTypes) {
    this.contexts[name]?.setError(this.state.interaction[name] && !this.state.validation[name]);
  }

  private updateProceedButton() {
    this.contexts.buttonProceed?.disabled(!this.state.validation.registrationNumber);
  }

  async onGetVehicle() {
    this.vehicle = undefined;
    this.requestError = undefined;
    this.contexts.requestError?.update(this.requestError);

    if (this.requestErrorNode) {
      this.requestErrorNode.style.display = 'none';
    }
    try {
      this.contexts.buttonProceed?.loading(true);
      const response = await getVehicle(
        this.props.settings.url,
        this.state.value.registrationNumber,
        this.props.settings.marketCode
      );
      this.vehicle = response;

      this.requestError = undefined;
    } catch (e) {
      this.requestError = e as RequestError;
    } finally {
      this.contexts.buttonProceed?.loading(false);
      this.contexts.requestError?.update(this.requestError);
      if (this.vehicle) {
        this.onProceed();
      }
    }
  }

  onProceed() {
    if (this.vehicle) {
      this.props.onProceed(
        {
          ...this.props.state,
          registrationNumber: this.state.value.registrationNumber,
          mileage: this.props.state.mileage,
        },
        this.vehicle
      );
    }
  }

  toggleExpanded() {
    this.expanded = !this.expanded;
    sessionStorage.setItem(
      'wayke-valuation-web-expanded',
      this.expanded ? 'expanded' : 'minimized'
    );
    const node = this.node.firstElementChild;
    if (node) {
      const hasExpandedClass = node.classList.contains(EXPANDED_CLASSNAME);
      if (this.expanded && !hasExpandedClass) {
        node.classList.add(EXPANDED_CLASSNAME);
      } else if (!this.expanded && hasExpandedClass) {
        node.classList.remove(EXPANDED_CLASSNAME);
      }
    }
  }

  render() {
    this.node.innerHTML = `
      <div class="waykevaluation-init ${this.expanded ? 'waykevaluation-init--expanded' : ''}">
        <div class="waykevaluation-init__shrinked">
          <div class="waykevaluation-init__header">
            <div class="waykevaluation-init__header-main">
              <div class="waykevaluation-hstack waykevaluation-hstack--spacing-1 waykevaluation-hstack--align-center">
                <div class="waykevaluation-hstack__item waykevaluation-hstack__item--no-shrink">
                  ${getSymbolLogotype({
                    market: this.props.settings.marketCode,
                    className: 'waykevaluation-init__logo',
                  })}
                </div>
                <div class="waykevaluation-hstack__item">
                  ${i18next.t('init.title')}
                </div>
              </div>
            </div>
            <button title="${i18next.t('init.expandButtonText', {
              brandName: i18next.t('global.brandName'),
            })}" class="waykevaluation-init__header-btn">
              <span class="waykevaluation-sr-only">
                ${i18next.t('init.expandButtonText', { brandName: i18next.t('global.brandName') })}
              </span>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 16 16"
                class="waykevaluation-icon"
                data-icon="Chevron up"
                aria-hidden="true"
              >
                <path d="m9.3 4.6 6.7 6.8-1.3 1.4L8 5.9l-6.7 6.9L0 11.4l6.7-6.9L8 3.2l1.3 1.4z"/>
              </svg>
            </button>
          </div>
        </div>
        <div class="waykevaluation-init__expanded">
          <header class="waykevaluation-init__header">
            <div class="waykevaluation-init__header-main">
              <div class="waykevaluation-heading waykevaluation-heading--4 waykevaluation-no-margin">
                ${i18next.t('init.expandedTitle')}
              </div>
            </div>
            <button title="${i18next.t('init.minifyButtonText', {
              brandName: i18next.t('global.brandName'),
            })}" class="waykevaluation-init__header-btn">
              <span class="waykevaluation-sr-only">
                ${i18next.t('init.minifyButtonText', { brandName: i18next.t('global.brandName') })}
              </span>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 16 16"
                class="waykevaluation-icon"
                data-icon="Chevron down"
                aria-hidden="true"
              >
                <path d="M6.7 11.4 0 4.6l1.3-1.4L8 10.1l6.7-6.9L16 4.6l-6.7 6.9L8 12.8l-1.3-1.4z" />
              </svg>
            </button>
          </header>
          <div class="waykevaluation-init__body">
            <div class="waykevaluation-stack waykevaluation-stack--1" id="${INPUT_REGISTRATION_NUMBER_NODE}"></div>
            <div class="waykevaluation-stack waykevaluation-stack--1" id="${REQUEST_ERROR_NODE}"></div>
            <div class="waykevaluation-stack waykevaluation-stack--1" id="${BUTTON_PROCEED_NODE}"></div>
          </div>
          <footer class="waykevaluation-init__footer">
            <div class="waykevaluation-hstack waykevaluation-hstack--spacing-1 waykevaluation-hstack--align-center">
              <div class="waykevaluation-hstack__item waykevaluation-hstack__item--no-shrink">
                ${getSymbolLogotype({
                  market: this.props.settings.marketCode,
                  className: 'waykevaluation-init__logo',
                })}
              </div>
              <div class="waykevaluation-hstack__item">
                ${i18next.t('init.association.body', {
                  link: `
                    <a href="${i18next.t('init.association.linkUrl')}" title="${i18next.t(
                    'init.association.linkTitle',
                    {
                      website: i18next.t('global.brandWebsiteUrl'),
                    }
                  )}" target="_blank" rel="noopener noreferer" class="waykevaluation-link waykevaluation-link--no-external-icon waykevaluation-link--current-color waykevaluation-link--underline">
                      ${i18next.t('global.brandName')}
                    </a>
                  `,
                  interpolation: { escapeValue: false },
                })}
              </div>
            </div>
          </footer>
        </div>
      </div>
    `;

    this.node.querySelectorAll('button').forEach((element) => {
      element.addEventListener('click', () => this.toggleExpanded());
    });

    this.contexts.registrationNumber = new InputFieldRegistrationNumber(
      document.querySelector<HTMLDivElement>(`#${INPUT_REGISTRATION_NUMBER_NODE}`),
      {
        title: i18next.t('input.registrationNumber.label'),
        value: this.state.value.registrationNumber,
        id: INPUT_REGISTRATION_NUMBER,
        error:
          this.state.interaction.registrationNumber && !this.state.validation.registrationNumber,
        errorMessage: i18next.t('input.registrationNumber.error') || undefined,
        name: 'registrationNumber',
        autocomplete: 'off',
        placeholder: i18next.t('input.registrationNumber.placeholder') || undefined,
        market: this.props.settings.marketCode,
        onChange: (e) => this.onChange(e),
        onBlur: (e) => this.onBlur(e),
        onKeyDown: (e) => this.onKeyDown(e),
      }
    );

    this.contexts.buttonProceed = new ButtonArrowRight(
      document.querySelector<HTMLDivElement>(`#${BUTTON_PROCEED_NODE}`),
      {
        title: i18next.t('init.startButtonText'),
        disabled: !this.state.validation.registrationNumber,
        onClick: () => this.onGetVehicle(),
      }
    );

    this.contexts.requestError = new RequestStatusError(
      this.node.querySelector<HTMLElement>(`#${REQUEST_ERROR_NODE}`),
      {}
    );
  }
}

export default Preview;
